import { Link } from 'react-router-dom';
import { motion } from "framer-motion";

function SectionHeader({ eyebrow, title, subtitle }) {
  return (
    <div className="mx-auto max-w-3xl text-center">
      <div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
        {eyebrow}
      </div>
      <h2
        className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl"
        style={{ fontFamily: "var(--font-display)" }}
      >
        {title}
      </h2>
      <p className="mt-3 text-sm leading-7 text-[var(--text-secondary)] sm:text-base">{subtitle}</p>
    </div>
  );
}

function PillarCard({ title, items }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.35 }}
      transition={{ duration: 0.45 }}
      className="rounded-3xl border border-white/10 bg-[var(--gradient-card)] p-7 backdrop-blur-xl"
    >
      <div
        className="text-lg font-semibold tracking-tight"
        style={{ fontFamily: "var(--font-display)" }}
      >
        {title}
      </div>
      <ul className="mt-4 grid gap-2 text-sm text-[var(--text-secondary)]">
        {items.map((i) => (
          <li key={i} className="leading-6">
            {i}
          </li>
        ))}
      </ul>
    </motion.div>
  );
}

function IntegrationCard({ title, items, monoLine }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.35 }}
      transition={{ duration: 0.45 }}
      className="rounded-3xl border border-white/10 bg-white/[0.03] p-7 backdrop-blur-xl"
    >
      <div
        className="text-lg font-semibold tracking-tight"
        style={{ fontFamily: "var(--font-display)" }}
      >
        {title}
      </div>
      <ul className="mt-4 grid gap-2 text-sm text-[var(--text-secondary)]">
        {items.map((i) => (
          <li key={i} className="leading-6">
            {i}
          </li>
        ))}
      </ul>
      {monoLine ? (
        <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.02] p-4 font-mono text-xs text-[var(--text-secondary)]">
          {monoLine}
        </div>
      ) : null}
    </motion.div>
  );
}

export default function Capabilities() {
  return (
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-[var(--gradient-hero)] p-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(37,99,235,0.18),transparent_45%),radial-gradient(circle_at_80%_30%,rgba(6,182,212,0.10),transparent_55%),radial-gradient(circle_at_60%_80%,rgba(212,165,116,0.10),transparent_60%)]" />
        <div className="relative">
          <div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
            Capabilities
          </div>
          <h1
            className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Identity Use Cases & Capabilities
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-[var(--text-secondary)] sm:text-base">
            Core identity capabilities aligned to business outcomes—secure access, controlled privilege, governed
            entitlements, and security-operations-grade detection and response.
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center rounded-xl bg-[var(--accent-trust)] px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/20"
            >
              Request Capability Assessment
            </Link>
            <Link
              to="/architecture"
              className="inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/[0.02] px-5 py-3 text-sm font-semibold text-[var(--text-primary)] hover:bg-white/[0.05]"
            >
              View Reference Architecture
            </Link>
          </div>
        </div>
      </div>

      <div className="mt-14">
        <SectionHeader
          eyebrow="Core identity use cases"
          title="Capabilities that operationalize trust"
          subtitle="These are the foundational identity controls enterprises need to secure users, machines, APIs, workloads, and sensitive data access—across cloud, on-prem, hybrid, and sovereign environments."
        />

        <div className="mt-10 grid gap-4 lg:grid-cols-2">
          <PillarCard
            title="Access Management"
            items={[
              "Single Sign-On (SSO) across enterprise applications",
              "Multi-factor authentication (MFA) enforcement",
              "Adaptive/risk-based authentication using behavioral signals",
              "Passwordless authentication (FIDO2, passkeys, biometrics)",
              "Federated identity for B2B and Partner access",
            ]}
          />

          <PillarCard
            title="Identity Governance & Administration (IGA)"
            items={[
              "Joiner-mover-leaver lifecycle automation",
              "Role-based and attribute-based access control (RBAC/ABAC)",
              "Access certification and recertification campaigns",
              "Segregation of duties (SoD) enforcement",
              "Entitlement management and access request workflows",
            ]}
          />

          <PillarCard
            title="Privileged Access Management (PAM)"
            items={[
              "Just-in-time privileged access provisioning",
              "Session recording and monitoring",
              "Credential vaulting and rotation",
              "Break-glass emergency access procedures",
              "Privileged behavior analytics",
            ]}
          />

          <PillarCard
            title="Customer Identity (CIAM)"
            items={[
              "Self-service registration and profile management",
              "Consent and privacy preference management",
              "Progressive profiling for reduced friction",
              "Social login integration",
              "Fraud detection at authentication boundaries",
            ]}
          />
        </div>
      </div>

      <div className="mt-16">
        <SectionHeader
          eyebrow="Security operations integration"
          title="Identity signals that power the SOC"
          subtitle="Identity is not just access control—it's security telemetry. The CoE patterns below connect identity fabric capabilities to detection, investigation, and automated response."
        />

        <div className="mt-10 grid gap-4 lg:grid-cols-2">
          <IntegrationCard
            title="Identity Threat Detection & Response (ITDR)"
            items={[
              "Compromised credential detection",
              "Impossible travel and anomalous login patterns",
              "Lateral movement identification via authentication logs",
              "Service account abuse monitoring",
              "Identity-based attack path analysis",
            ]}
            monoLine="Credential Theft → Initial Access → Privilege Escalation → Lateral Movement → Data Exfiltration"
          />

          <IntegrationCard
            title="SIEM/SOAR Integration Points"
            items={[
              "Authentication event streaming to Chronicle via Kafka/Pub-Sub",
              "Identity context enrichment for security alerts",
              "Automated response playbooks (account lockout, MFA step-up, session termination)",
              "Correlation of identity events with endpoint and network telemetry",
            ]}
            monoLine="Identity events + endpoint + network telemetry → correlated detections → automated containment"
          />
        </div>
      </div>

      <div className="mt-16 rounded-3xl border border-white/10 bg-[var(--gradient-trust)] p-10 text-center">
        <h2
          className="text-2xl font-semibold tracking-tight sm:text-3xl"
          style={{ fontFamily: "var(--font-display)" }}
        >
          Turn identity controls into measurable outcomes
        </h2>
        <p className="mx-auto mt-3 max-w-2xl text-sm leading-7 text-[var(--text-secondary)] sm:text-base">
          Let's map these use cases to your current environment and produce a prioritized roadmap for risk reduction,
          audit readiness, and operational efficiency.
        </p>
        <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link
            to="/contact"
            className="inline-flex w-full items-center justify-center rounded-xl bg-[var(--accent-trust)] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/20 sm:w-auto"
          >
            Request Identity Risk Assessment
          </Link>
          <Link
            to="/offerings"
            className="inline-flex w-full items-center justify-center rounded-xl border border-white/10 bg-white/[0.02] px-6 py-3 text-sm font-semibold text-[var(--text-primary)] hover:bg-white/[0.05] sm:w-auto"
          >
            Explore Offerings
          </Link>
        </div>
      </div>
    </div>
  );
}
